import React from 'react'
import type { ContactInfoProps } from '@/features/enrollment'
import { formatPhone } from '@/shared/utils'

export default function ContactInfo({ enrollmentData, updateEnrollmentData, errors }: ContactInfoProps) {
  return (
    <>
      {/* Address */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Injured Party Address <span className="text-red-500">*</span>
        </label>
        <div className="space-y-3">
          <input 
            type="text" 
            name="injuredPartyAddressLine1"
            className={`input-field ${errors.injuredPartyAddressLine1 ? 'border-red-500' : ''}`} 
            placeholder="Address Line 1" 
            value={enrollmentData.injuredPartyAddressLine1} 
            onChange={(e) => updateEnrollmentData('injuredPartyAddressLine1', e.target.value)} 
          />
          {errors.injuredPartyAddressLine1 && (
            <p className="mt-1 text-sm text-red-600">{errors.injuredPartyAddressLine1}</p>
          )}
          <input type="text" className="input-field" placeholder="Address Line 2 (optional)" value={enrollmentData.injuredPartyAddressLine2} onChange={(e) => updateEnrollmentData('injuredPartyAddressLine2', e.target.value)} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <input 
              type="text" 
              className={`input-field ${errors.injuredPartyCity ? 'border-red-500' : ''}`} 
              placeholder="City" 
              value={enrollmentData.injuredPartyCity} 
              onChange={(e) => updateEnrollmentData('injuredPartyCity', e.target.value)} 
            />
            <select 
              className={`input-field ${errors.injuredPartyState ? 'border-red-500' : ''}`} 
              value={enrollmentData.injuredPartyState} 
              onChange={(e) => updateEnrollmentData('injuredPartyState', e.target.value)}
            >
              <option value="">Select State</option>
              <option value="AL">Alabama</option>
              <option value="AK">Alaska</option>
              <option value="AZ">Arizona</option>
              <option value="AR">Arkansas</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="CT">Connecticut</option>
              <option value="DE">Delaware</option>
              <option value="DC">District of Columbia</option>
              <option value="FL">Florida</option>
              <option value="GA">Georgia</option>
              <option value="HI">Hawaii</option>
              <option value="ID">Idaho</option>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="IA">Iowa</option>
              <option value="KS">Kansas</option>
              <option value="KY">Kentucky</option>
              <option value="LA">Louisiana</option>
              <option value="ME">Maine</option>
              <option value="MD">Maryland</option>
              <option value="MA">Massachusetts</option>
              <option value="MI">Michigan</option>
              <option value="MN">Minnesota</option>
              <option value="MS">Mississippi</option>
              <option value="MO">Missouri</option>
              <option value="MT">Montana</option>
              <option value="NE">Nebraska</option>
              <option value="NV">Nevada</option>
              <option value="NH">New Hampshire</option>
              <option value="NJ">New Jersey</option>
              <option value="NM">New Mexico</option>
              <option value="NY">New York</option>
              <option value="NC">North Carolina</option>
              <option value="ND">North Dakota</option>
              <option value="OH">Ohio</option>
              <option value="OK">Oklahoma</option>
              <option value="OR">Oregon</option>
              <option value="PA">Pennsylvania</option>
              <option value="RI">Rhode Island</option>
              <option value="SC">South Carolina</option>
              <option value="SD">South Dakota</option>
              <option value="TN">Tennessee</option>
              <option value="TX">Texas</option>
              <option value="UT">Utah</option>
              <option value="VT">Vermont</option>
              <option value="VA">Virginia</option>
              <option value="WA">Washington</option>
              <option value="WV">West Virginia</option>
              <option value="WI">Wisconsin</option>
              <option value="WY">Wyoming</option>
            </select>
            <input 
              type="text" 
              className={`input-field ${errors.injuredPartyZipCode ? 'border-red-500' : ''}`} 
              placeholder="ZIP Code" 
              value={enrollmentData.injuredPartyZipCode} 
              onChange={(e) => updateEnrollmentData('injuredPartyZipCode', e.target.value)} 
            />
          </div>
          {(errors.injuredPartyCity || errors.injuredPartyState || errors.injuredPartyZipCode) && (
            <div className="space-y-1">
              {errors.injuredPartyCity && <p className="text-sm text-red-600">{errors.injuredPartyCity}</p>}
              {errors.injuredPartyState && <p className="text-sm text-red-600">{errors.injuredPartyState}</p>}
              {errors.injuredPartyZipCode && <p className="text-sm text-red-600">{errors.injuredPartyZipCode}</p>}
            </div>
          )}
        </div>
        <p className="text-xs text-accent-500 mt-1">Use victim's address if 18+, parent's if minor.</p>
      </div>

      {/* Phone Numbers */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          {enrollmentData.relationToVictim === 'Myself' 
            ? 'Injured party phone number' 
            : 'Legal guardian\'s phone number'
          } <span className="text-red-500">*</span>
        </label>
        <input 
          type="tel" 
          className={`input-field mb-3 ${errors.phoneNumber ? 'border-red-500' : ''}`} 
          placeholder="(123) 456-7890" 
          value={enrollmentData.phoneNumber || ''} 
          onChange={(e) => {
            const formatted = formatPhone(e.target.value);
            updateEnrollmentData('phoneNumber', formatted);
          }}
          maxLength={14}
        />
        {errors.phoneNumber && (
          <p className="mt-1 text-sm text-red-600 mb-3">{errors.phoneNumber}</p>
        )}
        
        <label className="block text-sm font-medium text-accent-700 mb-2">Secondary Phone Number</label>
        <input 
          type="tel" 
          className="input-field" 
          placeholder="(123) 456-7890" 
          value={enrollmentData.secondaryPhoneNumber} 
          onChange={(e) => {
            const formatted = formatPhone(e.target.value);
            updateEnrollmentData('secondaryPhoneNumber', formatted);
          }}
          maxLength={14}
        />
      </div>

      {/* Caller Number Type */}
      <div>
        <label className="block text-sm font-medium text-accent-700 mb-2">
          Caller Number Provided <span className="text-red-500">*</span>
          {enrollmentData.callerNumberType === 'Cell Phone' && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-selected)
            </span>
          )}
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input type="radio" name="callerNumberType" value="Home" checked={enrollmentData.callerNumberType === 'Home'} onChange={() => updateEnrollmentData('callerNumberType', 'Home')} className="form-radio text-primary-600" />
            <span className="ml-2 text-accent-700">Home</span>
          </label>
          <label className="flex items-center">
            <input type="radio" name="callerNumberType" value="Cell Phone" checked={enrollmentData.callerNumberType === 'Cell Phone'} onChange={() => updateEnrollmentData('callerNumberType', 'Cell Phone')} className="form-radio text-primary-600" />
            <span className="ml-2 text-accent-700">Cell Phone</span>
          </label>
        </div>
        {errors.callerNumberType && (
          <p className="mt-1 text-sm text-red-600">{errors.callerNumberType}</p>
        )}
      </div>

      {/* Text Messaging Approval (conditional on Cell Phone) */}
      {enrollmentData.callerNumberType === 'Cell Phone' && (
        <div>
          <label className="block text-sm font-medium text-accent-700 mb-2">
            Do you approve receiving text messaging? <span className="text-red-500">*</span>
            <span className="ml-2 text-xs text-gray-500">
              (Default: Yes)
            </span>
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input 
                type="radio" 
                name="approvesTextMessaging" 
                value="true" 
                checked={enrollmentData.approvesTextMessaging === true} 
                onChange={() => updateEnrollmentData('approvesTextMessaging', true)} 
                className="form-radio text-primary-600" 
              />
              <span className="ml-2 text-accent-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input 
                type="radio" 
                name="approvesTextMessaging" 
                value="false" 
                checked={enrollmentData.approvesTextMessaging === false} 
                onChange={() => updateEnrollmentData('approvesTextMessaging', false)} 
                className="form-radio text-primary-600" 
              />
              <span className="ml-2 text-accent-700">No</span>
            </label>
          </div>
          {errors.approvesTextMessaging && (
            <p className="mt-1 text-sm text-red-600">{errors.approvesTextMessaging}</p>
          )}
        </div>
      )}
    </>
  )
}
